(() => {
  const FORM_SELECTOR = '[data-tradepro-search-form]';
  const PANEL_SELECTOR = '[data-tradepro-search-panel]';
  const OPTION_SELECTOR = '[data-tradepro-search-option]';
  const formStates = new WeakMap();
  const initializedForms = new WeakSet();
  const openForms = new Set();

  const rootPath = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root
    : '/';

  const normalizedRootPath = rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
  const endpoint = (path) => normalizedRootPath + String(path || '').replace(/^\//, '');

  const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[char]);

  const escapeAttribute = escapeHTML;

  const appendImageWidth = (url, width = 160) => {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const getImageUrl = (product) => {
    if (!product) return '';
    if (typeof product.image === 'string') return product.image;
    if (product.image && product.image.url) return product.image.url;
    if (product.featured_image && typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && product.featured_image.url) return product.featured_image.url;
    return '';
  };

  const getImageAlt = (product) => {
    if (!product) return '';
    if (product.featured_image && product.featured_image.alt) return product.featured_image.alt;
    return product.title || '';
  };

  const getPrice = (product) => {
    if (!product) return '';
    return product.price || product.price_min || product.min_price || '';
  };

  const formatPrice = (value, currency) => {
    if (value === '' || value === null || typeof value === 'undefined') return '';
    const numericValue = Number.parseFloat(String(value));
    if (!Number.isFinite(numericValue)) return String(value);

    try {
      return new Intl.NumberFormat(document.documentElement.lang || navigator.language || 'en', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(numericValue);
    } catch (error) {
      return `${numericValue.toFixed(2)} ${currency || ''}`.trim();
    }
  };

  const createOverlay = () => {
    let overlay = document.querySelector('[data-tradepro-search-overlay]');
    if (overlay) return overlay;

    overlay = document.createElement('button');
    overlay.type = 'button';
    overlay.className = 'tradepro-predictive-search-overlay';
    overlay.setAttribute('aria-label', (window.KitomasgotaI18n?.text("Close search") ?? 'Close search'));
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('data-tradepro-search-overlay', '');
    overlay.tabIndex = -1;
    document.body.appendChild(overlay);
    return overlay;
  };

  const createPanel = (form) => {
    let panel = form.querySelector(PANEL_SELECTOR);
    if (panel) return panel;

    panel = document.createElement('div');
    panel.className = 'tradepro-predictive-search';
    panel.setAttribute('data-tradepro-search-panel', '');
    panel.setAttribute('aria-hidden', 'true');
    panel.id = `TradeProPredictiveSearch-${Math.random().toString(36).slice(2)}`;
    form.appendChild(panel);
    return panel;
  };

  const updateBodyState = () => {
    const hasAdvancedOpen = Array.from(openForms).some((form) => {
      const state = formStates.get(form);
      return state && state.isAdvanced && state.panel.classList.contains('is-open');
    });
    document.body.classList.toggle('tradepro-predictive-search-open', hasAdvancedOpen);
    const overlay = document.querySelector('[data-tradepro-search-overlay]');
    if (overlay) overlay.setAttribute('aria-hidden', hasAdvancedOpen ? 'false' : 'true');
  };

  const resetActiveOption = (state) => {
    state.activeIndex = -1;
    state.options.forEach((option) => {
      option.classList.remove('is-active');
      option.setAttribute('aria-selected', 'false');
    });
    state.input.removeAttribute('aria-activedescendant');
  };

  const refreshOptions = (state) => {
    state.options = Array.from(state.panel.querySelectorAll(OPTION_SELECTOR));
    state.options.forEach((option, index) => {
      if (!option.id) option.id = `${state.panel.id}-option-${index + 1}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.tabIndex = -1;
    });
    resetActiveOption(state);
  };

  const setActiveOption = (state, nextIndex) => {
    if (!state.options.length) return;

    const normalized = ((nextIndex % state.options.length) + state.options.length) % state.options.length;
    state.activeIndex = normalized;

    state.options.forEach((option, index) => {
      const isActive = index === normalized;
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    const activeOption = state.options[normalized];
    state.input.setAttribute('aria-activedescendant', activeOption.id);
    activeOption.scrollIntoView({ block: 'nearest' });
  };

  const announce = (state, message) => {
    if (state.status) state.status.textContent = message || '';
  };

  const openPanel = (state) => {
    if (!state.panel.innerHTML.trim()) return;

    openForms.forEach((form) => {
      if (form === state.form) return;
      const otherState = formStates.get(form);
      if (otherState) closePanel(otherState, { cancelPending: true });
    });

    state.panel.classList.add('is-open');
    state.panel.setAttribute('aria-hidden', 'false');
    state.input.setAttribute('aria-expanded', 'true');
    openForms.add(state.form);

    if (state.isAdvanced) {
      const overlay = createOverlay();
      overlay.setAttribute('aria-label', state.settings.closeLabel);
      document.dispatchEvent(new CustomEvent('tradepro:search-open', { detail: { form: state.form } }));
    }

    updateBodyState();
  };

  const cancelPendingSearch = (state) => {
    if (!state) return;
    state.requestVersion += 1;
    window.clearTimeout(state.timer);
    if (state.controller) state.controller.abort();
    state.controller = null;
    state.panel.removeAttribute('aria-busy');
  };

  const closePanel = (state, options = {}) => {
    if (!state) return;
    if (options.cancelPending) cancelPendingSearch(state);
    state.panel.classList.remove('is-open');
    state.panel.setAttribute('aria-hidden', 'true');
    state.input.setAttribute('aria-expanded', 'false');
    resetActiveOption(state);
    openForms.delete(state.form);
    updateBodyState();

    if (options.focusInput) {
      state.suppressFocusOpen = true;
      state.input.focus({ preventScroll: true });
      window.queueMicrotask(() => { state.suppressFocusOpen = false; });
    }
  };

  const closeAllPanels = (exceptForm = null) => {
    const forms = new Set([...document.querySelectorAll(FORM_SELECTOR), ...openForms]);
    forms.forEach((form) => {
      if (form === exceptForm) return;
      const state = formStates.get(form);
      if (state) closePanel(state, { cancelPending: true });
    });
  };

  const renderLoading = (state) => {
    state.panel.setAttribute('aria-busy', 'true');

    if (state.isAdvanced) {
      state.panel.innerHTML = `
        <div class="tradepro-predictive-search__header">
          <p class="tradepro-predictive-search__title">${escapeHTML(state.settings.title)}</p>
          <button class="tradepro-predictive-search__close" type="button" aria-label="${escapeAttribute(state.settings.closeLabel)}" data-tradepro-search-close>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"></path></svg>
          </button>
        </div>
        <div class="tradepro-predictive-search__loading">${escapeHTML(state.settings.loadingText)}</div>
        <div id="${escapeAttribute(state.listboxId)}" role="listbox" aria-label="${escapeAttribute(state.settings.title)}"></div>
      `;
    } else {
      state.panel.innerHTML = `
        <div class="tradepro-predictive-search__header">
          <p class="tradepro-predictive-search__title">${escapeHTML(state.settings.title)}</p>
        </div>
        <div class="tradepro-predictive-search__loading">${escapeHTML(state.settings.loadingText)}</div>
        <div id="${escapeAttribute(state.listboxId)}" role="listbox" aria-label="${escapeAttribute(state.settings.title)}"></div>
      `;
    }

    refreshOptions(state);
    openPanel(state);
  };

  const renderEmpty = (state) => {
    state.panel.setAttribute('aria-busy', 'false');

    if (state.isAdvanced) {
      state.panel.innerHTML = `
        <div class="tradepro-predictive-search__header">
          <p class="tradepro-predictive-search__title">${escapeHTML(state.settings.title)}</p>
          <button class="tradepro-predictive-search__close" type="button" aria-label="${escapeAttribute(state.settings.closeLabel)}" data-tradepro-search-close>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"></path></svg>
          </button>
        </div>
        <div id="${escapeAttribute(state.listboxId)}" role="listbox" aria-label="${escapeAttribute(state.settings.title)}">
          <div class="tradepro-predictive-search__empty" role="option" aria-disabled="true">${escapeHTML(state.settings.emptyText)}</div>
        </div>
      `;
    } else {
      state.panel.innerHTML = `
        <div class="tradepro-predictive-search__header">
          <p class="tradepro-predictive-search__title">${escapeHTML(state.settings.title)}</p>
        </div>
        <div id="${escapeAttribute(state.listboxId)}" role="listbox" aria-label="${escapeAttribute(state.settings.title)}">
          <div class="tradepro-predictive-search__empty" role="option" aria-disabled="true">${escapeHTML(state.settings.emptyText)}</div>
        </div>
      `;
    }

    refreshOptions(state);
    openPanel(state);
    announce(state, state.settings.emptyText);
  };

  const productMarkup = (product, state) => {
    const image = getImageUrl(product);
    const imageAlt = getImageAlt(product);
    const price = formatPrice(getPrice(product), state.settings.currency);
    const url = product && product.url ? product.url : '#';
    const isSoldOut = product && product.available === false;

    return `
      <a class="tradepro-predictive-search__item" href="${escapeAttribute(url)}" data-tradepro-search-option>
        ${
          image
            ? `<img class="tradepro-predictive-search__image" src="${escapeAttribute(appendImageWidth(image))}" alt="${escapeAttribute(imageAlt)}" width="58" height="58" loading="lazy">`
            : '<span class="tradepro-predictive-search__image" aria-hidden="true"></span>'
        }
        <div class="tradepro-predictive-search__copy">
          <p class="tradepro-predictive-search__item-title">${escapeHTML(product.title || '')}</p>
          <p class="tradepro-predictive-search__meta">
            ${price ? `<span>${escapeHTML(price)}</span>` : `<span>${escapeHTML(state.settings.productLabel)}</span>`}
            ${isSoldOut ? `<span class="tradepro-predictive-search__sold-out">${escapeHTML(state.settings.soldOutLabel)}</span>` : ''}
          </p>
        </div>
      </a>
    `;
  };

  const collectionMarkup = (collection) => `
    <a class="tradepro-predictive-search__collection" href="${escapeAttribute(collection.url || '#')}" data-tradepro-search-option>
      <span>${escapeHTML(collection.title || '')}</span>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6"></path></svg>
    </a>
  `;

  const viewAllLabel = (settings, query) => {
    if (settings.viewAllTemplate && settings.viewAllTemplate.includes('__QUERY__')) {
      return settings.viewAllTemplate.replace('__QUERY__', query);
    }
    return settings.viewAllLabel;
  };

  const renderAdvancedResults = (state, products, collections, query) => {
    const productItems = products.map((product) => productMarkup(product, state)).join('');
    const collectionItems = collections.map(collectionMarkup).join('');
    const searchUrl = `${endpoint('search')}?q=${encodeURIComponent(query)}&type=product`;

    state.panel.innerHTML = `
      <div class="tradepro-predictive-search__header">
        <p class="tradepro-predictive-search__title">${escapeHTML(state.settings.title)}</p>
        <button class="tradepro-predictive-search__close" type="button" aria-label="${escapeAttribute(state.settings.closeLabel)}" data-tradepro-search-close>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"></path></svg>
        </button>
      </div>
      <div id="${escapeAttribute(state.listboxId)}" role="listbox" aria-label="${escapeAttribute(state.settings.title)}">
        <div class="tradepro-predictive-search__body">
          ${products.length ? `
            <section class="tradepro-predictive-search__group" role="group" aria-labelledby="${state.panel.id}-products-heading">
              <p class="tradepro-predictive-search__group-title" id="${state.panel.id}-products-heading">${escapeHTML(state.settings.productsTitle)}</p>
              <div class="tradepro-predictive-search__items">${productItems}</div>
            </section>
          ` : ''}
          ${collections.length ? `
            <section class="tradepro-predictive-search__group" role="group" aria-labelledby="${state.panel.id}-collections-heading">
              <p class="tradepro-predictive-search__group-title" id="${state.panel.id}-collections-heading">${escapeHTML(state.settings.collectionsTitle)}</p>
              <div class="tradepro-predictive-search__collections">${collectionItems}</div>
            </section>
          ` : ''}
        </div>
        <div class="tradepro-predictive-search__footer" role="group" aria-label="${escapeAttribute(viewAllLabel(state.settings, query))}">
          <a class="tradepro-predictive-search__all" href="${escapeAttribute(searchUrl)}" data-tradepro-search-option>
            ${escapeHTML(viewAllLabel(state.settings, query))}
          </a>
        </div>
      </div>
    `;

    state.panel.setAttribute('aria-busy', 'false');
    refreshOptions(state);
    openPanel(state);
    announce(state, `${products.length + collections.length} ${state.settings.title}`);
  };

  const renderLegacyResults = (state, products, query) => {
    const productItems = products.map((product) => productMarkup(product, state)).join('');
    const searchUrl = `${endpoint('search')}?q=${encodeURIComponent(query)}&type=product`;

    state.panel.innerHTML = `
      <div class="tradepro-predictive-search__header">
        <p class="tradepro-predictive-search__title">${escapeHTML(state.settings.title)}</p>
      </div>
      <div id="${escapeAttribute(state.listboxId)}" role="listbox" aria-label="${escapeAttribute(state.settings.title)}">
        <div class="tradepro-predictive-search__items">${productItems}</div>
        <div class="tradepro-predictive-search__footer" role="group" aria-label="${escapeAttribute(state.settings.viewAllLabel)}">
          <a class="tradepro-predictive-search__all" href="${escapeAttribute(searchUrl)}" data-tradepro-search-option>${escapeHTML(state.settings.viewAllLabel)}</a>
        </div>
      </div>
    `;

    state.panel.setAttribute('aria-busy', 'false');
    refreshOptions(state);
    openPanel(state);
    announce(state, `${products.length} ${state.settings.title}`);
  };

  const fetchResults = async (state, query, signal) => {
    const params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', state.isAdvanced ? 'product,collection' : 'product');
    params.set('resources[limit]', String(state.settings.limit));
    params.set('resources[limit_scope]', state.isAdvanced ? 'each' : 'all');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku,variants.barcode');

    const response = await fetch(`${endpoint('search/suggest.json')}?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      signal
    });

    if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

    const data = await response.json();
    const results = data && data.resources && data.resources.results ? data.resources.results : {};

    return {
      products: Array.isArray(results.products) ? results.products.slice(0, state.settings.limit) : [],
      collections: Array.isArray(results.collections)
        ? results.collections.slice(0, state.settings.collectionLimit)
        : []
    };
  };

  const runSearch = async (state, query, activeVersion) => {
    if (activeVersion !== state.requestVersion) return;

    if (state.cache.has(query)) {
      const cached = state.cache.get(query);
      if (activeVersion !== state.requestVersion) return;
      if (state.isAdvanced) renderAdvancedResults(state, cached.products, cached.collections, query);
      else renderLegacyResults(state, cached.products, query);
      return;
    }

    if (state.controller) state.controller.abort();
    state.controller = new AbortController();
    const signal = state.controller.signal;
    renderLoading(state);

    try {
      const results = await fetchResults(state, query, signal);
      if (signal.aborted || activeVersion !== state.requestVersion) return;
      state.controller = null;

      if (!results.products.length && !results.collections.length) {
        renderEmpty(state);
        return;
      }

      state.cache.set(query, results);
      if (state.isAdvanced) renderAdvancedResults(state, results.products, results.collections, query);
      else renderLegacyResults(state, results.products, query);
    } catch (error) {
      if (signal.aborted || (error && error.name === 'AbortError') || activeVersion !== state.requestVersion) return;
      state.controller = null;
      renderEmpty(state);
    }
  };

  const moveActiveOption = (state, direction) => {
    if (!state.panel.classList.contains('is-open') && state.panel.innerHTML.trim()) openPanel(state);
    if (!state.options.length) return;
    const startIndex = state.activeIndex < 0 ? (direction > 0 ? 0 : state.options.length - 1) : state.activeIndex + direction;
    setActiveOption(state, startIndex);
  };

  const initializeForm = (form) => {
    if (initializedForms.has(form)) return;

    const input = form.querySelector('[data-tradepro-search-input]');
    if (!input) return;

    initializedForms.add(form);

    const panel = createPanel(form);
    const listboxId = `${panel.id}-listbox`;
    const clearButton = form.querySelector('[data-tradepro-search-clear]');
    const status = form.querySelector('[data-tradepro-search-status]');
    const isAdvanced = form.dataset.searchMode === 'header';

    input.setAttribute('aria-controls', listboxId);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('role', 'combobox');

    const state = {
      form,
      input,
      panel,
      listboxId,
      clearButton,
      status,
      isAdvanced,
      controller: null,
      timer: null,
      requestVersion: 0,
      activeIndex: -1,
      options: [],
      cache: new Map(),
      suppressFocusOpen: false,
      settings: {
        title: form.dataset.searchTitle || (window.KitomasgotaI18n?.text("Quick results") ?? 'Quick results'),
        productsTitle: form.dataset.searchProductsTitle || 'Products',
        collectionsTitle: form.dataset.searchCollectionsTitle || 'Collections',
        emptyText: form.dataset.searchEmpty || (window.KitomasgotaI18n?.text("No matching products found.") ?? 'No matching products found.'),
        viewAllLabel: form.dataset.searchViewAll || (window.KitomasgotaI18n?.text("View all results") ?? 'View all results'),
        viewAllTemplate: form.dataset.searchViewAllTemplate || '',
        productLabel: form.dataset.searchProductLabel || '',
        soldOutLabel: form.dataset.searchSoldOut || (window.KitomasgotaI18n?.text("Sold out") ?? 'Sold out'),
        loadingText: form.dataset.searchLoading || (window.KitomasgotaI18n?.text("Searching…") ?? 'Searching…'),
        closeLabel: form.dataset.searchClose || (window.KitomasgotaI18n?.text("Close search") ?? 'Close search'),
        clearLabel: form.dataset.searchClear || (window.KitomasgotaI18n?.text("Clear search") ?? 'Clear search'),
        limit: Math.min(10, Math.max(1, Number(form.dataset.searchLimit || 6))),
        collectionLimit: Math.min(10, Math.max(1, Number(form.dataset.searchCollectionLimit || 3))),
        currency: form.dataset.searchCurrency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR'
      }
    };

    formStates.set(form, state);

    const syncClearButton = () => {
      if (!clearButton) return;
      clearButton.hidden = input.value.length === 0;
    };

    syncClearButton();

    input.addEventListener('input', () => {
      const query = input.value.trim();
      const activeVersion = ++state.requestVersion;
      window.clearTimeout(state.timer);
      syncClearButton();
      resetActiveOption(state);

      if (query.length < 2) {
        if (state.controller) state.controller.abort();
        state.controller = null;
        state.panel.removeAttribute('aria-busy');
        state.panel.innerHTML = '';
        closePanel(state);
        announce(state, '');
        return;
      }

      state.timer = window.setTimeout(() => runSearch(state, query, activeVersion), 220);
    });

    input.addEventListener('focus', () => {
      if (state.suppressFocusOpen) {
        state.suppressFocusOpen = false;
        return;
      }
      if (input.value.trim().length >= 2 && panel.innerHTML.trim()) openPanel(state);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveActiveOption(state, 1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveActiveOption(state, -1);
        return;
      }

      if (event.key === 'Enter' && state.activeIndex >= 0 && state.options[state.activeIndex]) {
        event.preventDefault();
        const activeOption = state.options[state.activeIndex];
        const href = activeOption.getAttribute('href');
        if (href) window.location.assign(href);
        return;
      }

      if (event.key === 'Escape') {
        closePanel(state, { cancelPending: true });
      }
    });

    form.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!panel.classList.contains('is-open')) return;
      event.preventDefault();
      closePanel(state, { focusInput: true, cancelPending: true });
    });

    form.addEventListener('click', (event) => {
      const closeButton = event.target.closest('[data-tradepro-search-close]');
      if (closeButton) {
        event.preventDefault();
        closePanel(state, { focusInput: true, cancelPending: true });
      }
    });

    form.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!form.contains(document.activeElement)) closePanel(state, { cancelPending: true });
      }, 0);
    });

    if (clearButton) {
      clearButton.setAttribute('aria-label', state.settings.clearLabel);
      clearButton.addEventListener('click', () => {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      });
    }
  };

  const initializeForms = (root = document) => {
    if (root.matches && root.matches(FORM_SELECTOR)) initializeForm(root);
    root.querySelectorAll(FORM_SELECTOR).forEach(initializeForm);
  };

  initializeForms();

  document.addEventListener('shopify:section:load', (event) => initializeForms(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const forms = [];
    if (event.target.matches && event.target.matches(FORM_SELECTOR)) forms.push(event.target);
    event.target.querySelectorAll(FORM_SELECTOR).forEach((form) => forms.push(form));

    forms.forEach((form) => {
      const state = formStates.get(form);
      if (state) {
        closePanel(state, { cancelPending: true });
        formStates.delete(form);
      }
      initializedForms.delete(form);
      openForms.delete(form);
    });

    updateBodyState();
  });

  document.addEventListener('pointerdown', (event) => {
    if (event.target.closest(FORM_SELECTOR)) return;
    closeAllPanels();
  }, true);

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-tradepro-search-overlay]')) return;
    closeAllPanels();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeAllPanels();
  });

  document.addEventListener('tradepro:navigation-open', () => closeAllPanels());
})();
